import React, { useState, useMemo } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

/**
 * Helper to parse a date string or object into a standard Date object
 */
function parseEntryDate(entry) {
  if (entry.fullDate) {
    const d = new Date(entry.fullDate);
    if (!isNaN(d.getTime())) return d;
  }
  if (entry.date) {
    const d = new Date(entry.date);
    if (!isNaN(d.getTime())) return d;
  }
  return null;
}

/**
 * Format month-year like "Jan '24" or "Aug '26"
 */
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function formatMonthYear(d) {
  const m = MONTH_NAMES[d.getMonth()];
  const y = String(d.getFullYear()).slice(-2);
  return `${m} '${y}`;
}

function formatMonthFull(d) {
  return `${MONTH_FULL[d.getMonth()]} ${d.getFullYear()}`;
}

export default function PerformanceChart({ chartData, activeTab = 'This Month', setActiveTab }) {
  const [tab, setTab] = useState(activeTab || 'This Month');
  const currentTab = setActiveTab ? activeTab : tab;

  const handleTab = (t) => {
    setTab(t);
    if (setActiveTab) setActiveTab(t);
  };

  const rawData = useMemo(() => {
    if (Array.isArray(chartData) && chartData.length > 0) {
      return chartData;
    }
    return [
      { date: 'Aug 1', fullDate: '2026-08-01', bookings: 2, dutySlips: 2, invoices: 1 },
      { date: 'Aug 4', fullDate: '2026-08-04', bookings: 3, dutySlips: 2, invoices: 0 },
      { date: 'Aug 7', fullDate: '2026-08-07', bookings: 1, dutySlips: 2, invoices: 0 },
      { date: 'Aug 10', fullDate: '2026-08-10', bookings: 1, dutySlips: 0, invoices: 1 },
      { date: 'Aug 13', fullDate: '2026-08-13', bookings: 0, dutySlips: 3, invoices: 2 },
      { date: 'Aug 17', fullDate: '2026-08-17', bookings: 4, dutySlips: 5, invoices: 4 },
      { date: 'Aug 18', fullDate: '2026-08-18', bookings: 2, dutySlips: 2, invoices: 3 },
      { date: 'Aug 21', fullDate: '2026-08-21', bookings: 1, dutySlips: 1, invoices: 0 }
    ];
  }, [chartData]);

  // 1. Filter raw data based on selected tab
  const tabFilteredData = useMemo(() => {
    if (!rawData || rawData.length === 0) return [];

    if (currentTab === 'Last 7 Days') {
      return rawData.slice(-7);
    }
    if (currentTab === 'Last 30 Days') {
      return rawData.slice(-30);
    }
    if (currentTab === 'This Month') {
      // Find latest year/month in rawData or current month
      const now = new Date();
      const latestEntry = rawData[rawData.length - 1];
      const latestDate = latestEntry ? parseEntryDate(latestEntry) : now;
      const targetYear = latestDate ? latestDate.getFullYear() : now.getFullYear();
      const targetMonth = latestDate ? latestDate.getMonth() : now.getMonth();

      const matched = rawData.filter((d) => {
        const entryD = parseEntryDate(d);
        if (!entryD) return true;
        return entryD.getFullYear() === targetYear && entryD.getMonth() === targetMonth;
      });

      return matched.length > 0 ? matched : rawData.slice(-31);
    }

    // "All" tab -> returns all items in current filter range
    return rawData;
  }, [rawData, currentTab]);

  // 2. Intelligent Range Binning / Aggregation
  const { displayData, binMode } = useMemo(() => {
    const data = tabFilteredData;
    const count = data.length;

    // Case A: <= 35 data points (e.g. 7 days, 30 days, 1 month) -> Daily
    if (count <= 35) {
      const formatted = data.map((d) => ({
        ...d,
        displayDate: d.date || d.fullDate || 'N/A',
        tooltipTitle: d.fullDate || d.date || 'Day',
        bookings: Number(d.bookings) || 0,
        dutySlips: Number(d.dutySlips) || 0,
        invoices: Number(d.invoices) || 0
      }));
      return { displayData: formatted, binMode: 'daily' };
    }

    // Case B: 36 to 120 data points (~1 to 4 months) -> Weekly aggregation
    if (count <= 120) {
      const weeks = [];
      const chunkSize = 7;
      for (let i = 0; i < count; i += chunkSize) {
        const chunk = data.slice(i, i + chunkSize);
        const first = chunk[0];
        const last = chunk[chunk.length - 1];

        const startLabel = first.date || first.fullDate || '';
        const endLabel = last.date || last.fullDate || '';
        const label = chunk.length === 1 ? startLabel : `${startLabel} - ${endLabel.split(' ')[1] || endLabel}`;

        const totalBookings = chunk.reduce((sum, item) => sum + (Number(item.bookings) || 0), 0);
        const totalDutySlips = chunk.reduce((sum, item) => sum + (Number(item.dutySlips) || 0), 0);
        const totalInvoices = chunk.reduce((sum, item) => sum + (Number(item.invoices) || 0), 0);

        weeks.push({
          displayDate: label,
          tooltipTitle: `Week: ${startLabel} to ${endLabel}`,
          bookings: totalBookings,
          dutySlips: totalDutySlips,
          invoices: totalInvoices
        });
      }
      return { displayData: weeks, binMode: 'weekly' };
    }

    // Case C: > 120 data points (Multi-Month / Multi-Year, e.g. 2023 to 2026) -> Group by Month
    const monthMap = new Map();

    data.forEach((item) => {
      const d = parseEntryDate(item);
      let key;
      let monthLabel;
      let fullLabel;

      if (d) {
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        monthLabel = formatMonthYear(d);
        fullLabel = formatMonthFull(d);
      } else {
        key = item.date || 'Unknown';
        monthLabel = item.date || 'Unknown';
        fullLabel = item.date || 'Unknown';
      }

      if (!monthMap.has(key)) {
        monthMap.set(key, {
          key,
          displayDate: monthLabel,
          tooltipTitle: fullLabel,
          bookings: 0,
          dutySlips: 0,
          invoices: 0
        });
      }

      const mEntry = monthMap.get(key);
      mEntry.bookings += Number(item.bookings) || 0;
      mEntry.dutySlips += Number(item.dutySlips) || 0;
      mEntry.invoices += Number(item.invoices) || 0;
    });

    const months = Array.from(monthMap.values());
    return { displayData: months, binMode: 'monthly' };
  }, [tabFilteredData]);

  // 3. Calculate dynamic maximum and Y-Axis domain
  const maxVal = useMemo(() => {
    if (!displayData || displayData.length === 0) return 4;
    return Math.max(
      ...displayData.map((d) => Math.max(d.bookings || 0, d.dutySlips || 0, d.invoices || 0)),
      4
    );
  }, [displayData]);

  const yDomainMax = Math.ceil(maxVal <= 5 ? maxVal + 1 : maxVal * 1.18);
  const tickCount = Math.min(6, Math.max(3, maxVal + 1));

  // Determine bar size and dot styling dynamically based on number of points
  const pointCount = displayData.length;
  const barSize = pointCount > 40 ? 6 : pointCount > 25 ? 10 : pointCount > 14 ? 14 : 18;
  const showDots = pointCount <= 35;

  return (
    <div className="dash-card">
      <div className="dash-card-header pb-1 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <h6 className="dash-card-title mb-0">Booking &amp; Duty Slip Performance</h6>
          {binMode !== 'daily' && (
            <span
              className="badge"
              style={{
                fontSize: '9.5px',
                fontWeight: '600',
                backgroundColor: '#f1f5f9',
                color: '#475569',
                border: '1px solid #e2e8f0',
                padding: '2px 6px'
              }}
            >
              {binMode === 'monthly' ? 'Monthly Grouped' : 'Weekly Grouped'}
            </span>
          )}
        </div>
        <div className="chart-tabs-wrapper">
          {['Last 7 Days', 'Last 30 Days', 'This Month', 'All'].map((t) => (
            <button
              key={t}
              type="button"
              className={'chart-tab-btn ' + (currentTab === t ? 'active' : '')}
              onClick={() => handleTab(t)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="dash-card-body pt-0" style={{ height: 215 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={displayData}
            margin={{ top: 12, right: 15, left: -20, bottom: 0 }}
          >
            <CartesianGrid stroke="#f1f5f9" vertical={false} />
            <XAxis
              dataKey="displayDate"
              tick={{ fontSize: 9, fill: '#64748b' }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickLine={false}
              minTickGap={16}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={[0, yDomainMax]}
              tickCount={tickCount}
              tick={{ fontSize: 9, fill: '#64748b' }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const dataObj = payload[0]?.payload || {};
                  return (
                    <div
                      style={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        padding: '8px 12px',
                        fontSize: '11px',
                        minWidth: '130px'
                      }}
                    >
                      <div
                        style={{
                          fontWeight: '700',
                          color: '#0f172a',
                          marginBottom: '6px',
                          borderBottom: '1px solid #f1f5f9',
                          paddingBottom: '4px'
                        }}
                      >
                        {dataObj.tooltipTitle || label}
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span style={{ color: '#064e48', fontWeight: '600' }}>
                          ● Bookings:
                        </span>
                        <span style={{ fontWeight: '700', color: '#0f172a' }}>
                          {dataObj.bookings ?? 0}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span style={{ color: '#0d7a71', fontWeight: '600' }}>
                          ● Duty Slips:
                        </span>
                        <span style={{ fontWeight: '700', color: '#0f172a' }}>
                          {dataObj.dutySlips ?? 0}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span style={{ color: '#dc2626', fontWeight: '600' }}>
                          ● Invoices:
                        </span>
                        <span style={{ fontWeight: '700', color: '#0f172a' }}>
                          {dataObj.invoices ?? 0}
                        </span>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend
              verticalAlign="top"
              align="center"
              iconType="square"
              wrapperStyle={{ fontSize: '10.5px', top: -5 }}
            />
            <Bar
              dataKey="bookings"
              name="Bookings"
              fill="#064e48"
              barSize={barSize}
              radius={[3, 3, 0, 0]}
            />
            <Bar
              dataKey="dutySlips"
              name="Duty Slips"
              fill="#0d7a71"
              barSize={barSize}
              radius={[3, 3, 0, 0]}
            />
            <Line
              type="monotone"
              dataKey="invoices"
              name="Invoices"
              stroke="#dc2626"
              strokeWidth={2.2}
              dot={
                showDots
                  ? { r: 3.2, fill: '#dc2626', strokeWidth: 0 }
                  : false
              }
              activeDot={{ r: 5 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
